import { Api } from 'src/api';
import { Logger } from 'src/utils/logger';

export interface VideoAnalysisTriggerRequest {
  video_url: string;
  request_type: 'blog' | 'ig_captions' | 'email_text';
  request_id: string;
  force?: boolean; // Add optional force parameter to bypass cache
}

export interface VideoAnalysisGetResultRequest {
  request_id: string;
  request_type: 'blog' | 'ig_captions' | 'email_text';
}

export interface VideoAnalysisResult {
  status: 'pending' | 'processing' | 'completed' | 'failed';
  blog?: string;
  ig_captions?: string;
  email_text?: string;
  description?: string; // fallback for compatibility
  error?: string;
}

export class VideoAnalysisService {
  /**
   * Clean generated content by removing RAW_CONTENT contamination
   */
  private static cleanGeneratedContent(content: string): string {
    if (!content) return content;
    
    // Strip everything after RAW_CONTENT: separator
    const rawContentIndex = content.indexOf('RAW_CONTENT:');
    if (rawContentIndex !== -1) {
      return content.substring(0, rawContentIndex).trim();
    }
    
    return content;
  }
  /**
   * Trigger video analysis with video URL and request type
   */
  static async triggerAnalysis(params: VideoAnalysisTriggerRequest): Promise<void> {
    Logger.debug('Triggering video analysis', 'VideoAnalysisService', {
      request_type: params.request_type,
      request_id: params.request_id,
      force: params.force || false
    });
    
    try {
      const response = await Api.http.post('/video-analysis/trigger', {
        video_url: params.video_url,
        request_type: params.request_type,
        request_id: params.request_id,
        force: params.force || false, // Include force parameter to bypass cache when needed
      });
      
      Logger.debug('Video analysis triggered successfully', 'VideoAnalysisService', { request_id: params.request_id });
    } catch (error: any) {
      Logger.error('Failed to trigger video analysis', 'VideoAnalysisService', {
        message: error?.message,
        status: error?.response?.status,
        request_id: params.request_id
      });
      
      // Provide more specific error messages
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      
      if (error?.response?.status === 404) {
        throw new Error('Video analysis endpoint not found. Please check if the backend supports this feature.');
      } else if (error?.response?.status === 400) {
        throw new Error('Invalid video URL or request parameters. Please check the video URL and content type.');
      } else if (error?.response?.status >= 500) {
        throw new Error('Server error occurred. Please try again later.');
      } else {
        throw new Error(`Failed to trigger video analysis: ${errorMessage}`);
      }
    }
  }

  /**
   * Get video analysis result
   */
  static async getResult(params: VideoAnalysisGetResultRequest): Promise<VideoAnalysisResult> {
    try {
      Logger.debug('Getting video analysis result', 'VideoAnalysisService', { request_id: params.request_id });
      
      const response = await Api.http.post('/video-analysis/get-result', {
        request_id: params.request_id,
        request_type: params.request_type,
      });

      // The response is already unwrapped by the axios interceptor
      const data = response.data || response;
      
      Logger.debug('Video analysis result retrieved', 'VideoAnalysisService', { 
        request_id: params.request_id, 
        status: data.status 
      });
      
      // Clean all content fields to remove RAW_CONTENT contamination
      const cleanedBlog = data.blog ? this.cleanGeneratedContent(data.blog) : data.blog;
      const cleanedIgCaptions = data.ig_captions ? this.cleanGeneratedContent(data.ig_captions) : data.ig_captions;
      const cleanedEmailText = data.email_text ? this.cleanGeneratedContent(data.email_text) : data.email_text;
      
      // Extract the content based on request type
      let description = '';
      if (params.request_type === 'blog' && cleanedBlog) {
        description = cleanedBlog;
      } else if (params.request_type === 'ig_captions' && cleanedIgCaptions) {
        description = cleanedIgCaptions;
      } else if (params.request_type === 'email_text' && cleanedEmailText) {
        description = cleanedEmailText;
      }
      
      return {
        status: data.status || 'failed',
        description,
        blog: cleanedBlog,
        ig_captions: cleanedIgCaptions,
        email_text: cleanedEmailText,
        error: data.error,
      };
    } catch (error: any) {
      Logger.error('Failed to get video analysis result', 'VideoAnalysisService', error);
      throw new Error('Failed to get analysis result');
    }
  }

  /**
   * Get human-readable label for request type
   */
  static getRequestTypeLabel(type: VideoAnalysisTriggerRequest['request_type']): string {
    switch (type) {
      case 'blog':
        return 'Blog Post';
      case 'ig_captions':
        return 'IG Caption';
      case 'email_text':
        return 'Email Text';
      default:
        return type;
    }
  }
  
  /**
   * Generate a unique request ID for video analysis
   */
  static generateRequestId(type: VideoAnalysisTriggerRequest['request_type']): string {
    const timestamp = Date.now();
    return `${type}-${timestamp}`;
  }
}

// Legacy support for old interface names
export type { VideoAnalysisTriggerRequest as VideoAnalysisParams };

// Legacy instruction type mapping for backward compatibility
export const mapInstructionTypeToRequestType = (instructionType: string): VideoAnalysisTriggerRequest['request_type'] => {
  switch (instructionType) {
    case 'blog_post':
      return 'blog';
    case 'ig_caption':
      return 'ig_captions';
    case 'email_text':
      return 'email_text';
    default:
      return 'blog'; // fallback
  }
};